"use client";

import { recordIdentityLifecycleSignalOnceForSqid } from "@analytics/lib/auth-lifecycle";
import { readSqSidCookie } from "@analytics/lib/identity-cookie";
import { IDENTITY_LIFECYCLE_SIGNALS } from "@repo/identity";
import { useCallback } from "react";
import { SAAS_PRODUCT_EVENTS } from "./saas-product-events";
import { useAnalytics } from "./use-analytics";

const AUTH_SIGNUP_LIFECYCLE_STORAGE_KEY =
	"sq:identity:lifecycle:auth_signup_outcome_success";

export function useAuthAnalytics() {
	const { trackEvent } = useAnalytics();

	const authSignupOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(SAAS_PRODUCT_EVENTS.authSignupOutcome, props);
			if (props.result === "success") {
				const sqid = readSqSidCookie()?.trim();
				if (sqid) {
					recordIdentityLifecycleSignalOnceForSqid(
						sqid,
						IDENTITY_LIFECYCLE_SIGNALS.authSessionEstablished,
						AUTH_SIGNUP_LIFECYCLE_STORAGE_KEY,
					);
				}
			}
		},
		[trackEvent],
	);

	const authLoginOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(SAAS_PRODUCT_EVENTS.authLoginOutcome, props);
		},
		[trackEvent],
	);

	const authTwoFactorOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(SAAS_PRODUCT_EVENTS.authTwoFactorOutcome, props);
		},
		[trackEvent],
	);

	const authOauthStarted = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(SAAS_PRODUCT_EVENTS.authOauthStarted, props);
		},
		[trackEvent],
	);

	const authSessionEstablished = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(SAAS_PRODUCT_EVENTS.authSessionEstablished, props);
		},
		[trackEvent],
	);

	const authPasswordResetOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(SAAS_PRODUCT_EVENTS.authPasswordResetOutcome, props);
		},
		[trackEvent],
	);

	return {
		authSignupOutcome,
		authLoginOutcome,
		authTwoFactorOutcome,
		authOauthStarted,
		authSessionEstablished,
		authPasswordResetOutcome,
	};
}
